import React, { createContext, useCallback, useContext, useState } from "react";
import { Role, getListaRole } from "../../services/roleService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";

interface RoleContextProps {
  roles: Role[] | null;
  loading: boolean;
  error: string | null;
  fetchRole: () => void;
}

interface RoleProviderProps {
  children: React.ReactNode;
}

const RoleContext = createContext<RoleContextProps | null>(null);

const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRole = useCallback(async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaRole();
      setRoles(response.values || []);
    } catch (err) {
      setError(getErrorMessage("fetchRoleError"));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <RoleContext.Provider 
      value={{ 
        roles, 
        loading, 
        error, 
        fetchRole, 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </RoleContext.Provider>
  );
};

export default RoleProvider;

export const useRole = (): RoleContextProps => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useRole?.name,
        providerName: RoleProvider?.name,
      })
    );
  }
  return context;
};
